import { Button } from '@/components/ui/button';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { vehicleLocationStore } from '@/models/stores/index';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import useVehicleLocationMutations from './use-vehicle-location-mutations';

type VehicleLocationSelectorProps = {
  dealId: string;
  dealShortId: string;
  vehicleCurrentLocationId: Deal['vehicle_current_location_id'];
  className?: string;
};

const VehicleLocationSelector = observer(
  ({
    dealId,
    dealShortId,
    vehicleCurrentLocationId,
    className,
  }: VehicleLocationSelectorProps) => {
    const [comboBoxPopoverIsOpen, setComboBoxPopoverIsOpen] = useState(false);
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [newLocationName, setNewLocationName] = useState('');

    const vehicleLocations = vehicleLocationStore.all;

    const {
      createVehicleLocationMutation,
      updateVehicleCurrentLocationMutation,
    } = useVehicleLocationMutations({
      dealId,
      dealShortId,
    });

    const vehicleLocationOptions: Option<string>[] = useMemo(
      () => [
        ...vehicleLocations.map((location) => ({
          value: location.id,
          label: location.name,
        })),
      ],
      [vehicleLocations],
    );

    const currentOption = useMemo(() => {
      if (!vehicleCurrentLocationId) {
        return undefined;
      }
      return vehicleLocationOptions.find(
        (option) => option.value === vehicleCurrentLocationId,
      );
    }, [vehicleLocationOptions, vehicleCurrentLocationId]);

    const handleLocationChange = (value: string) => {
      if (value === 'none') {
        updateVehicleCurrentLocationMutation.mutate({
          locationId: '',
        });
        return;
      }

      updateVehicleCurrentLocationMutation.mutate({
        locationId: value,
      });
    };

    const handleCreateLocation = () => {
      if (!newLocationName.trim()) return;

      createVehicleLocationMutation.mutate({
        location: {
          id: uuidv4(),
          name: newLocationName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      });

      setNewLocationName('');
      setDialogIsOpen(false);
    };

    const currentLocationName = vehicleCurrentLocationId
      ? (vehicleLocationStore.getById(vehicleCurrentLocationId)?.name ??
        'Unknown Location')
      : 'No Location';

    return (
      <div>
        <ComboboxPopover
          endAdornment={<></>}
          open={comboBoxPopoverIsOpen}
          onOpenChange={setComboBoxPopoverIsOpen}
          placeholder="Set location..."
          onChange={handleLocationChange}
          emptyMessage="No location found"
          currentOption={currentOption}
          options={vehicleLocationOptions}
          footerOptions={
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setComboBoxPopoverIsOpen(false);
                  setDialogIsOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                <p className="rounded text-xs text-gray-600">
                  Create new location
                </p>
              </CommandItem>
            </CommandGroup>
          }
          side="right"
          align="start"
        >
          <div className={className}>
            <Button
              variant="ghost"
              className={cn('p-1 h-fit text-gray-500 text-xs', className)}
            >
              {currentLocationName}
            </Button>
          </div>
        </ComboboxPopover>
        <Dialog open={dialogIsOpen} onOpenChange={setDialogIsOpen}>
          <DialogContent onClickClose={() => setDialogIsOpen(false)}>
            <DialogHeader>
              <DialogTitle>Create New Location</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Input
                  id="location-name"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateLocation();
                    }
                  }}
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDialogIsOpen(false);
                    setNewLocationName('');
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateLocation}>Create Location</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
);

export default VehicleLocationSelector;
